import {
    AcademicCapIcon,
    ArrowDownTrayIcon,
    ArrowTrendingUpIcon,
    CalendarDaysIcon,
    ClockIcon,
    CurrencyRupeeIcon,
    EyeIcon,
    UserGroupIcon,
    UsersIcon
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { toast } from 'sonner';
import { LEAD_TABS, getLeadTab } from '../lib/leadTabs';
import { fetchJsonOrThrow, getApiErrorMessage } from '../lib/apiError';
import { downloadCsv, formatDateForCsv, formatDateTimeForCsv } from '../lib/csvExport';
import CRMAnalytics from './crm/CRMAnalytics';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from './ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './ui/table';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Get token from localStorage
const getToken = () => {
  return localStorage.getItem('admin_token');
};

// Helper to build authorized fetch options
const withAuth = (options = {}) => {
  const token = getToken();
  const baseHeaders = options.headers || {};

  return {
    ...options,
    headers: {
      ...baseHeaders,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.body && !baseHeaders['Content-Type'] ? { 'Content-Type': 'application/json' } : {})
    }
  };
};

// API service functions — backend error messages passed through fetchJsonOrThrow
const dashboardAPI = {
  getStats: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/stats`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch stats');
  },
  getCourseEnrollment: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/course-enrollment`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch course enrollment');
  },
  getMonthlyRevenue: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/monthly-revenue`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch monthly revenue');
  },
  getPaymentDistribution: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/payment-distribution`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch payment distribution');
  },
  getEMIData: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/emi-data`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch EMI data');
  },
  sendReminder: async (studentId, installmentNo) => {
    const response = await fetch(`${API_BASE_URL}/dashboard/send-reminder`, withAuth({
      method: 'POST',
      body: JSON.stringify({ studentId, installmentNo }),
    }));
    return fetchJsonOrThrow(response, 'Failed to send reminder');
  },
  getLeadStudentSummary: async () => {
    const response = await fetch(`${API_BASE_URL}/dashboard/lead-student-summary`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch lead/student summary');
  },
  getActiveLeads: async (search = '', page = 1, limit = 20) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page);
    params.append('limit', limit);
    const response = await fetch(`${API_BASE_URL}/dashboard/active-leads?${params.toString()}`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch active leads');
  },
  getEnrolledStudents: async (search = '', page = 1, limit = 20) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page);
    params.append('limit', limit);
    const response = await fetch(`${API_BASE_URL}/dashboard/enrolled-students?${params.toString()}`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch enrolled students');
  },
  getPendingFeeStudents: async (search = '', page = 1, limit = 20) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page);
    params.append('limit', limit);
    const response = await fetch(`${API_BASE_URL}/dashboard/pending-fee-students?${params.toString()}`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch pending fee students');
  },
  getStudentsWithoutEnrollment: async (search = '', page = 1, limit = 20) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page);
    params.append('limit', limit);
    const response = await fetch(`${API_BASE_URL}/dashboard/students-without-enrollment?${params.toString()}`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch students without enrollment');
  },
  getLeadsSnapshot: async () => {
    const params = new URLSearchParams();
    params.append('page', 1);
    params.append('limit', 500);
    params.append('sortBy', 'createdAt');
    params.append('sortOrder', 'desc');
    const response = await fetch(`${API_BASE_URL}/leads?${params.toString()}`, withAuth());
    return fetchJsonOrThrow(response, 'Failed to fetch leads snapshot');
  }
};

// Enhanced Shimmer Animation Component
const ShimmerEffect = ({ className = "", colorClass = "bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" }) => (
  <div className={`animate-shimmer bg-[length:200%_100%] ${colorClass} ${className}`}></div>
);

// Bubbly Stat Card Skeleton
const StatCardSkeleton = ({ color }) => (
  <Card className="card-hover overflow-hidden relative">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1 space-y-3">
          <div className="h-4 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%]"></div>
          <div className="h-8 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-pulse-scale"></div>
          <div className="h-3 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%] delay-75"></div>
        </div>
        <div className={`p-3 rounded-full bg-gradient-to-br ${color} opacity-20 animate-float`}>
          <div className="w-6 h-6 bg-white/50 rounded"></div>
        </div>
      </div>
      <div className="flex items-center mt-4 space-x-2 animate-slide-in-left">
        <div className="h-4 w-4 bg-green-200 rounded animate-bounce"></div>
        <div className="h-3 w-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full"></div>
        <div className="h-3 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full"></div>
      </div>
      <div className="absolute top-2 right-2 w-2 h-2 bg-blue-200 rounded-full animate-ping"></div>
      <div className="absolute bottom-4 left-4 w-1 h-1 bg-violet-200 rounded-full animate-ping delay-300"></div>
    </CardContent>
  </Card>
);

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color, loading }) => {
  if (loading) return <StatCardSkeleton color={color} />;

  return (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`} className="card-hover h-full">
      <CardContent className="p-5 h-full flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">{title}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
            </div>
            <div className={`p-2.5 rounded-full bg-gradient-to-br ${color}`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
          </div>
          {subtitle && (
            <p className="text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
        {trend && (
          <div className="flex items-center mt-3 pt-2 border-t border-gray-100">
            <ArrowTrendingUpIcon className="h-3.5 w-3.5 text-green-500 mr-1" />
            <span className="text-xs text-green-600 font-medium">{trend}</span>
            <span className="text-xs text-gray-500 ml-1">vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Color palette cycled across lead tab cards
const LEAD_TAB_COLORS = [
  'from-blue-500 to-blue-600',
  'from-violet-500 to-violet-600',
  'from-emerald-500 to-emerald-600',
  'from-amber-500 to-amber-600',
  'from-rose-500 to-rose-600',
  'from-sky-500 to-sky-600',
  'from-indigo-500 to-indigo-600',
  'from-teal-500 to-teal-600',
  'from-orange-500 to-orange-600',
  'from-pink-500 to-pink-600',
];

// Single Lead Tab Card — styled identically to StatCard
const LeadTabCard = ({ tab, count, colorClass, loading }) => {
  const navigate = useNavigate();

  if (loading) return <StatCardSkeleton color={colorClass} />;

  return (
    <Card
      className="card-hover h-full cursor-pointer"
      onClick={() => navigate(`/crm?tab=${tab.value}`)}
    >
      <CardContent className="p-5 h-full flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">{tab.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
            </div>
            <div className={`p-2.5 rounded-full bg-gradient-to-br ${colorClass}`}>
              <UserGroupIcon className="h-5 w-5 text-white" />
            </div>
          </div>
          <p className="text-xs text-gray-500">Click to view full list</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Chart Skeleton with Bars Animation
const ChartSkeleton = ({ type = "bar" }) => (
  <div className="w-full h-[250px] relative bg-gradient-to-br from-blue-50/30 via-violet-50/30 to-blue-50/30 rounded-xl overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer-slow bg-[length:200%_100%]"></div>
    <div className="relative h-full flex items-end justify-around p-6 gap-3">
      {type === "bar" ? (
        <>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full bg-gradient-to-t from-blue-400 to-violet-400 rounded-t-lg animate-grow-bar opacity-60"
                style={{ height: `${Math.random() * 50 + 30}%`, animationDelay: `${i * 150}ms` }}
              ></div>
              <div className="w-10 h-1.5 bg-gray-300 rounded-full animate-pulse"></div>
            </div>
          ))}
        </>
      ) : type === "line" ? (
        <div className="w-full h-full flex items-center justify-center">
          <svg className="w-full h-full" viewBox="0 0 400 180">
            <path d="M 0 140 Q 100 90 200 110 T 400 70" fill="none" stroke="url(#gradient)" strokeWidth="2.5" className="animate-draw-line" />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 0.6 }} />
                <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 0.6 }} />
              </linearGradient>
            </defs>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <circle key={i} cx={i * 70} cy={140 - Math.random() * 70} r="3.5" fill="#8b5cf6" className="animate-bounce" style={{ animationDelay: `${i * 200}ms` }} />
            ))}
          </svg>
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="relative w-40 h-40">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-300 to-violet-300 animate-spin-slow opacity-60"></div>
            <div className="absolute inset-6 rounded-full bg-white"></div>
            <div className="absolute inset-10 rounded-full bg-gradient-to-br from-green-300 to-orange-300 animate-pulse"></div>
          </div>
        </div>
      )}
    </div>
    <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center space-x-2">
      <div className="flex space-x-1">
        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"></div>
        <div className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce delay-100"></div>
        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce delay-200"></div>
      </div>
      <span className="text-xs text-gray-500 font-medium">Loading chart</span>
    </div>
  </div>
);

// Enhanced Recent Enrollments Skeleton
const EnrollmentItemSkeleton = ({ delay = 0 }) => (
  <div
    className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm border border-gray-100 animate-slide-in-right"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex-1 space-y-1.5">
      <div className="h-3.5 bg-gradient-to-r from-blue-200 via-violet-200 to-blue-200 rounded-full w-28 animate-shimmer bg-[length:200%_100%]"></div>
      <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full w-20 animate-shimmer bg-[length:200%_100%]"></div>
    </div>
    <div className="text-right space-y-1.5">
      <div className="h-3.5 bg-gradient-to-r from-green-200 via-green-300 to-green-200 rounded-lg w-14 animate-pulse-scale"></div>
      <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full w-16 animate-shimmer bg-[length:200%_100%]"></div>
    </div>
    <div className="absolute -right-1 -top-1 w-2 h-2 bg-violet-300 rounded-full animate-ping opacity-50"></div>
  </div>
);

const RecentEnrollmentsTable = ({ enrollments, loading }) => {
  const navigate = useNavigate();

  return (
    <Card data-testid="recent-enrollments-card" className="h-full flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-base">
          <ClockIcon className="h-4 w-4 mr-2 text-blue-600" />
          Recent Enrollments
        </CardTitle>
        <CardDescription className="text-xs">Latest student course registrations</CardDescription>
      </CardHeader>
      <CardContent className="flex-1">
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((item) => (
              <EnrollmentItemSkeleton key={item} delay={item * 100} />
            ))}
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-[210px] overflow-y-auto pr-1 scrollbar-thin">
              {enrollments.map((enrollment) => (
                <div key={enrollment.id} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{enrollment.studentName}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{enrollment.courseName}</p>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <p className="text-sm font-medium text-[#6F00FF]">₹{enrollment.amount.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{new Date(enrollment.date).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={() => navigate('/students')} variant="outline" size="sm" className="w-full mt-3 border-[#6F00FF] text-[#6F00FF] hover:bg-[#F5F0FF] hover:text-[#6F00FF] text-xs">
              <EyeIcon className="h-3.5 w-3.5 mr-1.5" />
              View All Enrollments
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Enhanced Table Skeleton for EMI dialog
const TableRowSkeleton = ({ cols = 6, delay = 0 }) => (
  <TableRow className="animate-fade-in" style={{ animationDelay: `${delay}ms` }}>
    {Array.from({ length: cols }).map((_, i) => (
      <TableCell key={i}>
        <div
          className={`h-4 rounded-full animate-shimmer bg-[length:200%_100%] ${
            i === 2 ? 'bg-gradient-to-r from-green-200 via-green-300 to-green-200 w-16' :
            i === cols - 1 ? 'bg-gradient-to-r from-purple-200 via-purple-300 to-purple-200 w-24' :
            'bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 w-20'
          }`}
        ></div>
      </TableCell>
    ))}
  </TableRow>
);

// Add custom animations
const style = document.createElement('style');
style.textContent = `
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes shimmer-slow { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes pulse-scale { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.05); opacity: 0.8; } }
  @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
  @keyframes slide-in-left { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes slide-in-right { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  @keyframes grow-bar { from { transform: scaleY(0); } to { transform: scaleY(1); } }
  @keyframes draw-line { from { stroke-dasharray: 1000; stroke-dashoffset: 1000; } to { stroke-dasharray: 1000; stroke-dashoffset: 0; } }
  @keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
  .animate-shimmer { animation: shimmer 2s ease-in-out infinite; }
  .animate-shimmer-slow { animation: shimmer-slow 3s ease-in-out infinite; }
  .animate-pulse-scale { animation: pulse-scale 2s ease-in-out infinite; }
  .animate-float { animation: float 3s ease-in-out infinite; }
  .animate-slide-in-left { animation: slide-in-left 0.5s ease-out; }
  .animate-slide-in-right { animation: slide-in-right 0.5s ease-out; }
  .animate-grow-bar { animation: grow-bar 1s ease-out; transform-origin: bottom; }
  .animate-draw-line { animation: draw-line 2s ease-out infinite; }
  .animate-spin-slow { animation: spin-slow 3s linear infinite; }
  .animate-fade-in { animation: fade-in 0.5s ease-out; }
  .delay-75 { animation-delay: 75ms; }
  .delay-100 { animation-delay: 100ms; }
  .delay-200 { animation-delay: 200ms; }
  .delay-300 { animation-delay: 300ms; }
  .scrollbar-thin::-webkit-scrollbar { width: 4px; }
  .scrollbar-thin::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
  .scrollbar-thin::-webkit-scrollbar-thumb { background: #c5b5e6; border-radius: 10px; }
  .scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #9f8cd6; }
`;
document.head.appendChild(style);

export default function Dashboard() {
  usePageMeta(PAGE_META.dashboard);
  const [stats, setStats] = useState(null);
  const [courseEnrollmentData, setCourseEnrollmentData] = useState([]);
  const [monthlyRevenueData, setMonthlyRevenueData] = useState([]);
  const [paymentDistribution, setPaymentDistribution] = useState([]);
  const [emiData, setEmiData] = useState({ pendingEMIs: [], thisMonthClosed: [] });
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [emiLoading, setEmiLoading] = useState(false);
  const navigate = useNavigate();
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [leadTabsLoading, setLeadTabsLoading] = useState(false);
  const [leadTabCounts, setLeadTabCounts] = useState({});
  const [leadStudentSummary, setLeadStudentSummary] = useState({
    activeLeadsCount: 0,
    enrolledStudentsCount: 0,
    pendingFeeStudentsCount: 0,
    studentsWithoutEnrollmentCount: 0
  });

  // Modal state
  const [activeLeadsModalOpen, setActiveLeadsModalOpen] = useState(false);
  const [enrolledStudentsModalOpen, setEnrolledStudentsModalOpen] = useState(false);
  const [pendingFeeModalOpen, setPendingFeeModalOpen] = useState(false);
  const [noEnrollmentModalOpen, setNoEnrollmentModalOpen] = useState(false);

  const [activeLeadsState, setActiveLeadsState] = useState({ loading: false, search: '', items: [], page: 1, total: 0 });
  const [enrolledStudentsState, setEnrolledStudentsState] = useState({ loading: false, search: '', items: [], page: 1, total: 0 });
  const [pendingFeeState, setPendingFeeState] = useState({ loading: false, search: '', items: [], page: 1, total: 0 });
  const [noEnrollmentState, setNoEnrollmentState] = useState({ loading: false, search: '', items: [], page: 1, total: 0 });
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const [leadAnalytics, setLeadAnalytics] = useState(null);
  const [taskAnalytics, setTaskAnalytics] = useState(null);

  useEffect(() => {
    loadDashboardData();
    loadLeadTabCounts();
    loadLeadAnalytics();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setChartsLoading(true);
      const [statsData, courseEnrollment, monthlyRevenue, paymentDist, leadStudentSummaryData] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getCourseEnrollment(),
        dashboardAPI.getMonthlyRevenue(),
        dashboardAPI.getPaymentDistribution(),
        dashboardAPI.getLeadStudentSummary()
      ]);
      setStats(statsData);
      setCourseEnrollmentData(courseEnrollment);
      setMonthlyRevenueData(monthlyRevenue);
      setPaymentDistribution(paymentDist);
      setLeadStudentSummary(leadStudentSummaryData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error(error.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setChartsLoading(false);
    }
  };

  const loadLeadTabCounts = async () => {
    try {
      setLeadTabsLoading(true);
      const snapshot = await dashboardAPI.getLeadsSnapshot();
      const leads = snapshot?.data || [];
      const counts = LEAD_TABS.reduce((acc, current) => {
        acc[current.value] = leads.filter((lead) => getLeadTab(lead) === current.value).length;
        return acc;
      }, {});
      setLeadTabCounts(counts);
    } catch (error) {
      console.error('Error loading lead tab counts:', error);
      toast.error(error.message || 'Failed to load lead status tabs');
    } finally {
      setLeadTabsLoading(false);
    }
  };

  const loadActiveLeads = async (search = '', page = 1) => {
    try {
      setActiveLeadsState(prev => ({ ...prev, loading: true, search, page }));
      const data = await dashboardAPI.getActiveLeads(search, page);
      setActiveLeadsState(prev => ({ ...prev, loading: false, items: data.leads || [], total: data.total || 0, page: data.page || 1 }));
    } catch (error) {
      console.error('Error loading active leads:', error);
      toast.error(error.message || 'Failed to load active leads');
      setActiveLeadsState(prev => ({ ...prev, loading: false }));
    }
  };

  const loadEnrolledStudents = async (search = '', page = 1) => {
    try {
      setEnrolledStudentsState(prev => ({ ...prev, loading: true, search, page }));
      const data = await dashboardAPI.getEnrolledStudents(search, page);
      setEnrolledStudentsState(prev => ({ ...prev, loading: false, items: data.students || [], total: data.total || 0, page: data.page || 1 }));
    } catch (error) {
      console.error('Error loading enrolled students:', error);
      toast.error(error.message || 'Failed to load enrolled students');
      setEnrolledStudentsState(prev => ({ ...prev, loading: false }));
    }
  };

  const loadPendingFeeStudents = async (search = '', page = 1) => {
    try {
      setPendingFeeState(prev => ({ ...prev, loading: true, search, page }));
      const data = await dashboardAPI.getPendingFeeStudents(search, page);
      setPendingFeeState(prev => ({ ...prev, loading: false, items: data.students || [], total: data.total || 0, page: data.page || 1 }));
    } catch (error) {
      console.error('Error loading pending fee students:', error);
      toast.error(error.message || 'Failed to load pending fee students');
      setPendingFeeState(prev => ({ ...prev, loading: false }));
    }
  };

  const loadNoEnrollmentStudents = async (search = '', page = 1) => {
    try {
      setNoEnrollmentState(prev => ({ ...prev, loading: true, search, page }));
      const data = await dashboardAPI.getStudentsWithoutEnrollment(search, page);
      setNoEnrollmentState(prev => ({ ...prev, loading: false, items: data.students || [], total: data.total || 0, page: data.page || 1 }));
    } catch (error) {
      console.error('Error loading students without enrollment:', error);
      toast.error(error.message || 'Failed to load students without enrollment');
      setNoEnrollmentState(prev => ({ ...prev, loading: false }));
    }
  };




  const loadLeadAnalytics = async () => {
    try {
      const [leadsRes, tasksRes] = await Promise.all([
        fetch(`${API_BASE_URL}/leads/stats/overview`, withAuth()),
        fetch(`${API_BASE_URL}/tasks/stats/overview`, withAuth()),
      ]);
      const leadsJson = await leadsRes.json();
      const tasksJson = await tasksRes.json();
      if (leadsJson.success) setLeadAnalytics(leadsJson.data);
      if (tasksJson.success) setTaskAnalytics(tasksJson.data);
    } catch (err) {
      console.error('Failed to load lead analytics:', err);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border rounded-lg shadow-lg text-xs">
          <p className="font-medium mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-xs">
              {entry.dataKey}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatCurrency = (amount) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  };

  return (
    <div className="space-y-4 p-4 text-[13px] max-w-full">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-blue-50 to-violet-50 rounded-lg p-4 border border-blue-100 animate-fade-in">
        <h1 className="text-xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-sm text-gray-600">Monitor your institute's performance and key metrics</p>
      </div>

      {/* ── COMMENTED OUT: Lead Status Tabs button card ──
      <Card className="animate-fade-in">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base">Lead Status Tabs</CardTitle>
          <CardDescription className="text-xs">
            Click any status to open full table view with search and filters
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {leadTabsLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 animate-pulse">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="h-9 rounded-md bg-gray-100" />
              ))}
            </div>
          ) : (
            <div className="w-full h-auto flex flex-wrap gap-2 justify-start">
              {LEAD_TABS.map((tab) => (
                <Button key={tab.value} variant="outline" size="sm" className="text-xs rounded-md"
                  onClick={() => navigate(`/dashboard/lead-status/${tab.value}`)}>
                  {tab.label} ({leadTabCounts[tab.value] || 0})
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      ── END COMMENTED OUT ── */}

      {/* ── COMMENTED OUT: Stats Grid (Total Students, Active Courses, Total Revenue, Active Leads) ──
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div onClick={() => navigate('/students')} className="cursor-pointer h-full">
          <StatCard title="Total Students" value={stats?.totalStudents || 0}
            subtitle={stats ? `${stats.paidStudents} paid, ${stats.emiStudents} on EMI` : ''}
            icon={UserGroupIcon} trend="+12%" color="from-blue-500 to-blue-600" loading={loading} />
        </div>
        <div onClick={() => navigate('/courses')} className="cursor-pointer h-full">
          <StatCard title="Active Courses" value={stats?.totalCourses || 0}
            subtitle="All courses running" icon={AcademicCapIcon} trend="+2"
            color="from-violet-500 to-violet-600" loading={loading} />
        </div>
        <div onClick={() => navigate('/fees')} className="cursor-pointer h-full">
          <StatCard title="Total Revenue" value={stats ? formatCurrency(stats.totalRevenue) : '₹0'}
            subtitle="Lifetime earnings" icon={CurrencyRupeeIcon} trend="+18%"
            color="from-green-500 to-green-600" loading={loading} />
        </div>
        <div onClick={() => navigate('/crm')} className="cursor-pointer h-full">
          <StatCard title="Active Leads" value={leadStudentSummary.activeLeadsCount || 0}
            subtitle="Open / in-progress" icon={UserGroupIcon} trend="+5%"
            color="from-amber-500 to-amber-600" loading={loading} />
        </div>
      </div>
      ── END COMMENTED OUT ── */}

      {/* ── COMMENTED OUT: Lead & Student Insights Cards (Active Leads, Enrolled Students, Pending Fee, No Enrollment) ──
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        ... (Active Leads, Enrolled Students, Pending Fee, No Enrollment cards)
      </div>
      ── END COMMENTED OUT ── */}

      {/* Lead Tab Cards — replaces all stat card rows above, styled identically to StatCard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {LEAD_TABS.map((tab, index) => (
          <LeadTabCard
            key={tab.value}
            tab={tab}
            count={leadTabCounts[tab.value] || 0}
            colorClass={LEAD_TAB_COLORS[index % LEAD_TAB_COLORS.length]}
            loading={leadTabsLoading}
          />
        ))}
      </div>

      {/* Lead Analytics */}
      <Card className="animate-fade-in">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base">Lead Analytics</CardTitle>
          <CardDescription className="text-xs">Pipeline overview, trends, sources, and course interest</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <CRMAnalytics stats={leadAnalytics} taskStats={taskAnalytics} />
        </CardContent>
      </Card>

      {/* Charts Row - 2 charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="course-enrollment-chart" className="animate-fade-in h-full">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-base">Course Enrollment Overview</CardTitle>
            <CardDescription className="text-xs">Students enrolled per course (Paid vs EMI)</CardDescription>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {chartsLoading ? (
              <ChartSkeleton type="bar" />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={courseEnrollmentData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="paid" stackId="a" fill="#3b82f6" name="Paid" barSize={20} />
                  <Bar dataKey="emi" stackId="a" fill="#8b5cf6" name="EMI" barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card data-testid="payment-distribution-chart" className="animate-fade-in h-full">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-base">Payment Distribution</CardTitle>
            <CardDescription className="text-xs">Breakdown of payment methods</CardDescription>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {chartsLoading ? (
              <ChartSkeleton type="pie" />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={paymentDistribution} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={5} dataKey="value">
                      {paymentDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center mt-2 space-x-3 flex-wrap">
                  {paymentDistribution.map((entry, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-2.5 h-2.5 rounded-full mr-1.5" style={{ backgroundColor: entry.color }}></div>
                      <span className="text-xs text-gray-600">{entry.name}: {entry.value}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend and Recent Enrollments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="revenue-trend-chart" className="animate-fade-in h-full">
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-base">Revenue Trend</CardTitle>
            <CardDescription className="text-xs">Monthly revenue vs targets (Last 6 months)</CardDescription>
          </CardHeader>
          <CardContent className="px-2 pb-3">
            {chartsLoading ? (
              <ChartSkeleton type="line" />
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthlyRevenueData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`} />
                  <Tooltip content={<CustomTooltip />} formatter={(value) => [`₹${(value / 1000).toFixed(0)}K`, '']} />
                  <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2.5} name="Revenue" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="target" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" name="Target" dot={{ r: 2 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <RecentEnrollmentsTable enrollments={stats?.recentEnrollments || []} loading={loading} />
      </div>

      {/* Quick Actions */}
      <Card className="animate-fade-in">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base">Quick Actions</CardTitle>
          <CardDescription className="text-xs">Commonly used features and exports</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Button data-testid="add-student-button" onClick={() => navigate('/students')}
              className="h-16 flex flex-col items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs">
              <UserGroupIcon className="h-5 w-5 mb-1" />
              Add Student
            </Button>
            <Button data-testid="add-course-button" onClick={() => navigate('/courses')} variant="outline"
              className="h-16 flex flex-col items-center justify-center border-violet-200 hover:bg-violet-50 text-xs">
              <AcademicCapIcon className="h-5 w-5 mb-1 text-violet-600" />
              Create Course
            </Button>
            <Button data-testid="view-payments-button" variant="outline"
              className="h-16 flex flex-col items-center justify-center border-green-200 hover:bg-green-50 text-xs"
              onClick={() => navigate('/fees')}>
              <CurrencyRupeeIcon className="h-5 w-5 mb-1 text-green-600" />
              Payment Reports
            </Button>
            <Button data-testid="export-csv-button" variant="outline"
              className="h-16 flex flex-col items-center justify-center border-amber-200 hover:bg-amber-50 text-xs"
              onClick={() => setExportModalOpen(true)}>
              <ArrowDownTrayIcon className="h-5 w-5 mb-1 text-amber-600" />
              Export to CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Export to CSV Modal */}
      <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Export to CSV</DialogTitle>
            <DialogDescription>Select date range (optional) and export type. Leave dates empty for all records.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">From Date</label>
                <input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">To Date</label>
                <input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)}
                  className="w-full border rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Export type</p>
              <div className="grid grid-cols-1 gap-2">
                <Button variant="outline" size="sm" className="justify-start" disabled={exporting}
                  onClick={async () => {
                    setExporting(true);
                    try {
                      const params = new URLSearchParams();
                      params.set('limit', '5000');
                      if (exportFrom) params.set('createdAtFrom', exportFrom);
                      if (exportTo) params.set('createdAtTo', exportTo);
                      const res = await fetch(`${API_BASE_URL}/leads?${params}`, withAuth());
                      const json = await fetchJsonOrThrow(res, 'Failed to export');
                      const data = json.data || [];
                      const headers = ['Name', 'Email', 'Phone', 'Course', 'Status', 'Priority', 'Source', 'Owner', 'Follow-up', 'Created'];
                      const rows = data.map((l) => [l.name || '-', l.email || '-', l.phone || '-', l.interestCourse?.name || '-', l.status || '-', l.priority || '-', l.source || '-', l.owner?.name || '-', l.followUpDate ? formatDateTimeForCsv(l.followUpDate) : '-', formatDateTimeForCsv(l.createdAt)]);
                      downloadCsv(headers, rows, `leads-${exportFrom || 'all'}-${exportTo || 'all'}.csv`);
                      toast.success(`Exported ${rows.length} leads`);
                    } catch (e) {
                      toast.error(getApiErrorMessage(e, 'Failed to export'));
                    } finally {
                      setExporting(false);
                    }
                  }}>
                  <UsersIcon className="h-4 w-4 mr-2" />
                  Leads / Enrollment
                </Button>
                <Button variant="outline" size="sm" className="justify-start" disabled={exporting}
                  onClick={async () => {
                    setExporting(true);
                    try {
                      const params = new URLSearchParams();
                      params.set('limit', '5000');
                      if (exportFrom) params.set('startDate', exportFrom);
                      if (exportTo) params.set('endDate', exportTo);
                      const res = await fetch(`${API_BASE_URL}/fees/export?${params}`, withAuth());
                      const json = await fetchJsonOrThrow(res, 'Failed to export');
                      const data = json.data || [];
                      const headers = ['Payment Date', 'Student', 'Email', 'Phone', 'Course', 'Batch', 'Amount', 'Mode', 'Receipt No', 'Installment', 'Recorded By'];
                      const rows = data.map((p) => [formatDateForCsv(p.paymentDate), p.studentName || '-', p.studentEmail || '-', p.studentPhone || '-', p.courseName || '-', p.batchName || '-', p.amount || 0, p.mode || '-', p.receiptNo || '-', p.installmentNo ?? '-', p.recordedBy || '-']);
                      downloadCsv(headers, rows, `fee-collection-${exportFrom || 'all'}-${exportTo || 'all'}.csv`);
                      toast.success(`Exported ${rows.length} fee records`);
                    } catch (e) {
                      toast.error(getApiErrorMessage(e, 'Failed to export'));
                    } finally {
                      setExporting(false);
                    }
                  }}>
                  <CurrencyRupeeIcon className="h-4 w-4 mr-2" />
                  Fee Collection
                </Button>
                <Button variant="outline" size="sm" className="justify-start"
                  onClick={() => { setExportModalOpen(false); navigate('/attendance'); toast.info('Use Export on Attendance page for batch & student attendance'); }}>
                  <CalendarDaysIcon className="h-4 w-4 mr-2" />
                  Attendance (Batch & Student)
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Active Leads Modal */}
      <Dialog open={activeLeadsModalOpen} onOpenChange={setActiveLeadsModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Active Leads</DialogTitle>
            <DialogDescription>Leads that are open, contacted, demo scheduled, or awaiting decision</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder="Search by name, email, or phone..."
              value={activeLeadsState.search} onChange={(e) => loadActiveLeads(e.target.value, 1)} />
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Lead</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Course</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Status</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Priority</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Next Follow-up</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeLeadsState.loading ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-gray-500">Loading leads...</TableCell></TableRow>
                  ) : activeLeadsState.items.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-6 text-gray-500">No active leads found</TableCell></TableRow>
                  ) : (
                    activeLeadsState.items.map((lead) => (
                      <TableRow key={lead.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div className="space-y-1">
                            <p className="text-sm font-medium text-gray-900">{lead.name}</p>
                            <p className="text-xs text-gray-500">{lead.email}</p>
                            <p className="text-xs text-gray-500">{lead.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{lead.interestCourseName || 'N/A'}</TableCell>
                        <TableCell className="text-xs capitalize">{lead.status}</TableCell>
                        <TableCell className="text-xs capitalize">{lead.priority}</TableCell>
                        <TableCell className="text-xs">{lead.followUpDate ? new Date(lead.followUpDate).toLocaleString() : 'Not set'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enrolled Students Modal */}
      <Dialog open={enrolledStudentsModalOpen} onOpenChange={setEnrolledStudentsModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Enrolled Students</DialogTitle>
            <DialogDescription>Students who are actively assigned to at least one batch</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder="Search by name, email, or phone..."
              value={enrolledStudentsState.search} onChange={(e) => loadEnrolledStudents(e.target.value, 1)} />
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Student</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Contact</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Source</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide text-center">Profile</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrolledStudentsState.loading ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-gray-500">Loading students...</TableCell></TableRow>
                  ) : enrolledStudentsState.items.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-gray-500">No enrolled students found</TableCell></TableRow>
                  ) : (
                    enrolledStudentsState.items.map((s) => (
                      <TableRow key={s.id} className="hover:bg-gray-50">
                        <TableCell className="text-sm font-medium text-gray-900">{s.name}</TableCell>
                        <TableCell className="text-xs">
                          <div className="space-y-1">
                            <p className="text-gray-600">{s.email}</p>
                            <p className="text-gray-600">{s.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs capitalize">{s.admissionSource || 'N/A'}</TableCell>
                        <TableCell className="text-xs text-center align-middle">
                          <div className="flex justify-center">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/students/${s.id}`)}>View</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending Fee Students Modal */}
      <Dialog open={pendingFeeModalOpen} onOpenChange={setPendingFeeModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Pending Fee Students</DialogTitle>
            <DialogDescription>Students who have outstanding fee amounts across any course</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder="Search by name, email, or phone..."
              value={pendingFeeState.search} onChange={(e) => loadPendingFeeStudents(e.target.value, 1)} />
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Student</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Contact</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Total</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Paid</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Pending</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide text-center">Profile</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingFeeState.loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-500">Loading students...</TableCell></TableRow>
                  ) : pendingFeeState.items.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-6 text-gray-500">No students with pending fees found</TableCell></TableRow>
                  ) : (
                    pendingFeeState.items.map((s) => (
                      <TableRow key={s.id} className="hover:bg-gray-50">
                        <TableCell className="text-sm font-medium text-gray-900">{s.name}</TableCell>
                        <TableCell className="text-xs">
                          <div className="space-y-1">
                            <p className="text-gray-600">{s.email}</p>
                            <p className="text-gray-600">{s.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-medium text-gray-900">{formatCurrency(s.totalAmount || 0)}</TableCell>
                        <TableCell className="text-xs text-green-600 font-medium">{formatCurrency(s.paidAmount || 0)}</TableCell>
                        <TableCell className="text-xs text-orange-600 font-medium">{formatCurrency(s.pendingAmount || 0)}</TableCell>
                        <TableCell className="text-xs text-center align-middle">
                          <div className="flex justify-center">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/students/${s.id}`)}>View</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Students Without Enrollment Modal */}
      <Dialog open={noEnrollmentModalOpen} onOpenChange={setNoEnrollmentModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Students Without Enrollment</DialogTitle>
            <DialogDescription>Active students who are not yet assigned to any batch</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input className="w-full border rounded-md px-3 py-2 text-sm" placeholder="Search by name, email, or phone..."
              value={noEnrollmentState.search} onChange={(e) => loadNoEnrollmentStudents(e.target.value, 1)} />
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Student</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Contact</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Source</TableHead>
                    <TableHead className="text-xs text-white uppercase tracking-wide">Profile</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {noEnrollmentState.loading ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-gray-500">Loading students...</TableCell></TableRow>
                  ) : noEnrollmentState.items.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center py-6 text-gray-500">No students without enrollment found</TableCell></TableRow>
                  ) : (
                    noEnrollmentState.items.map((s) => (
                      <TableRow key={s.id} className="hover:bg-gray-50">
                        <TableCell className="text-sm font-medium text-gray-900">{s.name}</TableCell>
                        <TableCell className="text-xs">
                          <div className="space-y-1">
                            <p className="text-gray-600">{s.email}</p>
                            <p className="text-gray-600">{s.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs capitalize">{s.admissionSource || 'N/A'}</TableCell>
                        <TableCell className="text-xs text-center align-middle">
                          <div className="flex justify-center">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/students/${s.id}`)}>Enroll</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}